import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User } from 'firebase/auth';
import {
  doc,
  onSnapshot,
  Unsubscribe,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { db } from './lib/firebase';

interface Profile {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  level: string;
  team: string;
  paymentStatus?: string;
  hasLifetimeAccess?: boolean;
  plan?: string;
  customerId?: string;
  lastPaymentDate?: number;
  gamesCreated?: number;
  analysesCreated?: number;
  createdAt: Date;
}

interface AdminLimits {
  freeGamesLimit: number;
  freeAnalysisLimit: number;
  updatedAt?: Date;
}

interface Store {
  user: User | null;
  profile: Profile | null;
  profileLoading: boolean;
  limits: AdminLimits;
  limitsLoading: boolean;
  countsSymced: boolean; // Flag to track if counts have been synced this session
  language: 'en' | 'fr';
  showUpgradeModal: boolean;
  setUser: (user: User | null) => void;
  setProfile: (profile: Profile | null) => void;
  setProfileLoading: (loading: boolean) => void;
  setLimits: (limits: AdminLimits) => void;
  setLimitsLoading: (loading: boolean) => void;
  setCountsSynced: (synced: boolean) => void;
  setLanguage: (language: 'en' | 'fr') => void;
  setShowUpgradeModal: (show: boolean) => void;
  subscribeToProfile: (userId: string) => Unsubscribe | null;
  subscribeToLimits: () => Unsubscribe | null;
  unsubscribeFromProfile: () => void;
  unsubscribeFromLimits: () => void;
  syncProfileCounts: (userId: string) => Promise<void>;
  getUsageInfo: (action: 'game' | 'analysis') => {
    allowed: boolean;
    hasPaid: boolean;
    usage: {
      used: number;
      limit: number;
    };
  } | null;
}

let profileUnsubscribe: Unsubscribe | null = null;
let limitsUnsubscribe: Unsubscribe | null = null;

export const useStore = create<Store>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      profileLoading: false,
      limits: {
        freeGamesLimit: 1, // Default fallback
        freeAnalysisLimit: 1, // Default fallback
      },
      limitsLoading: true,
      countsSymced: false,
      language: 'en', // Default to English
      showUpgradeModal: false,

      setUser: (user) => {
        set({ user });

        // Handle profile subscription when user changes
        const currentUnsubscribe = get().unsubscribeFromProfile;
        if (currentUnsubscribe) {
          currentUnsubscribe();
        }

        if (user) {
          // Subscribe to profile updates
          const unsubscribe = get().subscribeToProfile(user.uid);
          profileUnsubscribe = unsubscribe;
        } else {
          // Clear profile and reset sync flag when user logs out
          set({ profile: null, profileLoading: false, countsSymced: false });
        }
      },

      setProfile: (profile) => set({ profile }),
      setProfileLoading: (loading) => set({ profileLoading: loading }),
      setLimits: (limits) => set({ limits }),
      setLimitsLoading: (loading) => set({ limitsLoading: loading }),
      setCountsSynced: (synced) => set({ countsSymced: synced }),
      setLanguage: (language) => set({ language }),
      setShowUpgradeModal: (show) => set({ showUpgradeModal: show }),

      subscribeToProfile: (userId: string) => {
        try {
          set({ profileLoading: true });

          const unsubscribe = onSnapshot(
            doc(db, 'profiles', userId),
            (docSnapshot) => {
              if (docSnapshot.exists()) {
                const profileData = {
                  id: docSnapshot.id,
                  ...docSnapshot.data(),
                  createdAt:
                    docSnapshot.data().createdAt?.toDate() || new Date(),
                } as Profile;

                set({ profile: profileData, profileLoading: false });
              } else {
                set({ profile: null, profileLoading: false });
              }
            },
            (error) => {
              console.error('Profile subscription error:', error);
              set({ profileLoading: false });
            }
          );
          return unsubscribe;
        } catch (error) {
          console.error('Error setting up profile subscription:', error);
          set({ profileLoading: false });
          return null;
        }
      },

      subscribeToLimits: () => {
        try {
          set({ limitsLoading: true });

          // Clean up any existing subscription first
          if (limitsUnsubscribe) {
            limitsUnsubscribe();
            limitsUnsubscribe = null;
          }

          const unsubscribe = onSnapshot(
            doc(db, 'settings', 'limits'),
            (docSnapshot) => {
              if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                const limitsData = {
                  freeGamesLimit: data.freeGamesLimit || 1,
                  freeAnalysisLimit: data.freeAnalysisLimit || 1,
                  updatedAt: data.updatedAt?.toDate(),
                } as AdminLimits;

                set({ limits: limitsData, limitsLoading: false });
              } else {
                // Document doesn't exist, use defaults
                const defaultLimits = {
                  freeGamesLimit: 1,
                  freeAnalysisLimit: 1,
                };
                set({
                  limits: defaultLimits,
                  limitsLoading: false,
                });
              }
            },
            (error) => {
              console.error('Limits subscription error:', error);
              set({ limitsLoading: false });
            }
          );

          // Store the unsubscribe function globally
          limitsUnsubscribe = unsubscribe;
          return unsubscribe;
        } catch (error) {
          console.error('Error setting up limits subscription:', error);
          set({ limitsLoading: false });
          return null;
        }
      },

      unsubscribeFromProfile: () => {
        if (profileUnsubscribe) {
          profileUnsubscribe();
          profileUnsubscribe = null;
        }
      },

      unsubscribeFromLimits: () => {
        if (limitsUnsubscribe) {
          limitsUnsubscribe();
          limitsUnsubscribe = null;
        }
      },

      syncProfileCounts: async (userId: string) => {
        // Check if counts have already been synced this session
        const currentState = get();
        if (currentState.countsSymced) {
          return;
        }

        try {
          // Count actual games in database
          const gamesQuery = query(
            collection(db, 'games'),
            where('userId', '==', userId)
          );
          const gamesSnapshot = await getDocs(gamesQuery);
          const actualGameCount = gamesSnapshot.size;

          // Count actual AI trainer sessions in database
          const sessionsQuery = query(
            collection(db, 'ai_trainer_sessions'),
            where('userId', '==', userId)
          );
          const sessionsSnapshot = await getDocs(sessionsQuery);
          const actualAnalysisCount = sessionsSnapshot.size;

          const currentProfile = get().profile;
          if (!currentProfile) {
            return;
          }

          const currentGameCount = currentProfile.gamesCreated || 0;
          const currentAnalysisCount = currentProfile.analysesCreated || 0;

          // Update profile if counts don't match
          if (
            currentGameCount !== actualGameCount ||
            currentAnalysisCount !== actualAnalysisCount
          ) {
            await updateDoc(doc(db, 'profiles', userId), {
              gamesCreated: actualGameCount,
              analysesCreated: actualAnalysisCount,
            });
          }

          // Mark as synced for this session
          set({ countsSymced: true });
        } catch (error) {
          console.error('Error syncing profile counts:', error);
        }
      },

      getUsageInfo: (action: 'game' | 'analysis') => {
        const currentProfile = get().profile;
        const currentLimits = get().limits;

        if (!currentProfile) {
          return null;
        }

        const hasPaid = currentProfile.paymentStatus === 'paid';

        if (action === 'game') {
          const gamesCreated = currentProfile.gamesCreated || 0;
          return {
            allowed: hasPaid || gamesCreated < currentLimits.freeGamesLimit,
            hasPaid,
            usage: {
              used: gamesCreated,
              limit: currentLimits.freeGamesLimit,
            },
          };
        }

        const analysesCreated = currentProfile.analysesCreated || 0;
        return {
          allowed: hasPaid || analysesCreated < currentLimits.freeAnalysisLimit,
          hasPaid,
          usage: {
            used: analysesCreated,
            limit: currentLimits.freeAnalysisLimit,
          },
        };
      },
    }),
    {
      name: 'goalr-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        profile: state.profile,
        limits: state.limits,
        language: state.language,
      }),
    }
  )
);